<template>
  <div class="product-view">
    <div v-if="loading" class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
    </div>
    <v-alert v-else-if="error" type="error" text dense class="ma-4">{{ error }}</v-alert>

    <template v-else-if="product">
      <div class="pv-top">
        <!-- Images: the selected variant's, or every variant's when none is picked -->
        <div class="pv-gallery">
          <div class="pv-main-image">
            <img v-if="activeImage" :src="activeImage" :alt="product.name" @error="markBroken(activeImage)" />
            <div v-else class="pv-no-image">
              <v-icon size="48" color="grey lighten-1">mdi-image-off-outline</v-icon>
              <span>No image</span>
            </div>
          </div>
          <div v-if="images.length > 1" class="pv-thumbs">
            <button
              v-for="(img, i) in images"
              :key="img.key"
              type="button"
              class="pv-thumb"
              :class="{ 'pv-thumb--active': i === imageIndex }"
              @click="imageIndex = i"
            >
              <img :src="img.url" :alt="img.label" @error="markBroken(img.url)" />
            </button>
          </div>
        </div>

        <div class="pv-facts">
          <div class="pv-chips">
            <v-chip small :color="product.is_active ? 'success' : 'grey'" text-color="white" class="mr-2">
              <v-icon left x-small>{{ product.is_active ? "mdi-check-circle" : "mdi-pause-circle" }}</v-icon>
              {{ product.is_active ? "Active" : "Inactive" }}
            </v-chip>
            <v-chip v-if="categoryPath" small outlined color="secondary">
              <v-icon left x-small>mdi-shape-outline</v-icon>{{ categoryPath }}
            </v-chip>
          </div>

          <div class="pv-price">
            <span class="pv-price__value">{{ priceRange }}</span>
            <span class="pv-price__note">selling price{{ variants.length > 1 ? " across variants" : "" }}</span>
          </div>

          <div class="pv-stats">
            <div class="pv-stat">
              <span class="pv-stat__label">In stock</span>
              <span class="pv-stat__value" :class="{ 'error--text': totalStock === 0 }">{{ totalStock }}</span>
            </div>
            <div class="pv-stat">
              <span class="pv-stat__label">Variants</span>
              <span class="pv-stat__value">{{ variants.length }}</span>
            </div>
            <div class="pv-stat">
              <span class="pv-stat__label">Out of stock</span>
              <span class="pv-stat__value" :class="{ 'warning--text': outOfStock > 0 }">{{ outOfStock }}</span>
            </div>
          </div>

          <dl class="pv-details">
            <div><dt>HSN code</dt><dd>{{ product.hsn_code || "—" }}</dd></div>
            <div><dt>GST</dt><dd>{{ product.gst_value ? `${Number(product.gst_value)}%` : "—" }}</dd></div>
            <div>
              <dt>Delivery</dt>
              <dd>{{ product.delivery_type == 0 ? "Free delivery" : `Paid - ₹${money(product.delivery_amount)}` }}</dd>
            </div>
            <div><dt>Added</dt><dd>{{ addedOn }}</dd></div>
          </dl>
        </div>
      </div>

      <div v-if="product.description || product.product_details" class="pv-section">
        <h4 class="pv-section__title">About this product</h4>
        <p v-if="product.description" class="pv-text">{{ product.description }}</p>
        <p v-if="product.product_details" class="pv-text">{{ product.product_details }}</p>
      </div>

      <div class="pv-section">
        <h4 class="pv-section__title">
          Variants
          <span class="pv-section__hint">Click a row to see its images</span>
        </h4>
        <div class="pv-table-wrap">
          <table class="pv-table">
            <thead>
              <tr>
                <th>Colour</th>
                <th>Size</th>
                <th class="text-right">Price</th>
                <th class="text-right">Stock</th>
                <th>QR code</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="variant in variants"
                :key="variant.id"
                :class="{ 'pv-row--active': selectedVariantId === variant.id }"
                @click="selectVariant(variant)"
              >
                <td>
                  <span class="pv-swatch" :style="{ background: swatch(variant.color) }"></span>
                  {{ variant.color || "—" }}
                </td>
                <td>{{ variant.size || "—" }}</td>
                <td class="text-right">
                  <template v-if="hasDiscount(variant)">
                    <span class="pv-strike">₹{{ money(variant.price) }}</span>
                    <strong>₹{{ money(sellingPrice(variant)) }}</strong>
                  </template>
                  <strong v-else>₹{{ money(variant.price) }}</strong>
                </td>
                <td class="text-right">
                  <span class="pv-stock" :class="stockClass(stockOf(variant))">{{ stockOf(variant) }}</span>
                </td>
                <td><code class="pv-code">{{ variant.qr_code_data || "—" }}</code></td>
              </tr>
              <tr v-if="!variants.length">
                <td colspan="5" class="text-center grey--text py-6">No variants yet</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pv-actions">
        <v-btn text @click="$emit('close')">Close</v-btn>
        <v-spacer></v-spacer>
        <v-btn outlined color="success" class="mr-2" :to="`/products/${encryptIt(product.id)}/qr-codes`">
          <v-icon left small>mdi-qrcode</v-icon>QR codes
        </v-btn>
        <v-btn v-if="canEdit" depressed color="primary" @click="$emit('edit', product)">
          <v-icon left small>mdi-pencil</v-icon>Edit product
        </v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ProductView",
  props: {
    productId: { type: [Number, String], required: true },
    canEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      product: null,
      loading: true,
      error: null,
      selectedVariantId: null,
      imageIndex: 0,
      brokenImages: [], // files that failed to load - dropped so the placeholder shows instead
    };
  },
  computed: {
    // mediaUrl comes from the global mixin (settings/getMediaUrl)
    variants() {
      return (this.product && this.product.product_variants) || [];
    },
    images() {
      const source = this.selectedVariantId
        ? this.variants.filter((v) => v.id === this.selectedVariantId)
        : this.variants;
      return source
        .flatMap((v) =>
          (v.product_variant_images || []).map((img) => ({
            key: `${v.id}-${img.id}`,
            url: (this.mediaUrl || "") + img.image_path,
            label: [v.color, v.size].filter(Boolean).join(" / "),
          }))
        )
        .filter((img) => !this.brokenImages.includes(img.url));
    },
    activeImage() {
      const img = this.images[this.imageIndex] || this.images[0];
      return img ? img.url : null;
    },
    categoryPath() {
      const names = [];
      let category = this.product && this.product.category;
      while (category) {
        names.unshift(category.name);
        category = category.parent_category;
      }
      return names.join(" › ");
    },
    totalStock() {
      return this.variants.reduce((sum, v) => sum + this.stockOf(v), 0);
    },
    outOfStock() {
      return this.variants.filter((v) => this.stockOf(v) === 0).length;
    },
    priceRange() {
      const prices = this.variants.map((v) => this.sellingPrice(v));
      if (!prices.length) return "—";
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      return min === max ? `₹${this.money(min)}` : `₹${this.money(min)} – ₹${this.money(max)}`;
    },
    addedOn() {
      if (!this.product.created_at) return "—";
      const date = new Date(this.product.created_at).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      const by = this.product.created_by && this.product.created_by.user_name;
      return by ? `${date} by ${by}` : date;
    },
  },
  watch: {
    productId: "load",
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      this.selectedVariantId = null;
      this.imageIndex = 0;
      this.brokenImages = [];
      try {
        const response = await axios.get(`/products/${this.encryptIt(this.productId)}`);
        this.product = response.data.data;
      } catch (error) {
        console.log(error);
        this.error = (error.response && error.response.data && error.response.data.message) || "Could not load this product.";
      }
      this.loading = false;
    },
    markBroken(url) {
      if (!this.brokenImages.includes(url)) this.brokenImages.push(url);
      this.imageIndex = 0;
    },
    selectVariant(variant) {
      // Clicking the selected row again shows every variant's images
      this.selectedVariantId = this.selectedVariantId === variant.id ? null : variant.id;
      this.imageIndex = 0;
    },
    stockOf(variant) {
      // Laravel sends the companyStock relation as company_stock
      return variant.company_stock ? Number(variant.company_stock.quantity) || 0 : 0;
    },
    hasDiscount(variant) {
      return Number(variant.discount_amount) > 0 && Number(variant.discount_amount) < Number(variant.price);
    },
    sellingPrice(variant) {
      return this.hasDiscount(variant) ? Number(variant.discount_amount) : Number(variant.price) || 0;
    },
    stockClass(qty) {
      if (qty === 0) return "pv-stock--out";
      if (qty <= 5) return "pv-stock--low";
      return "pv-stock--ok";
    },
    swatch(color) {
      // Colour names like "SKY BLUE" become CSS colours when the browser knows them
      const css = String(color || "").toLowerCase().replace(/\s+/g, "");
      if (css && typeof CSS !== "undefined" && CSS.supports("color", css)) return css;
      return "linear-gradient(135deg, #e0dcd5, #b8b1a6)";
    },
    money(value) {
      return (Number(value) || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.product-view {
  padding: 4px 4px 0;
}
.pv-top {
  display: grid;
  grid-template-columns: minmax(0, 320px) minmax(0, 1fr);
  gap: 24px;
}
@media (max-width: 760px) {
  .pv-top {
    grid-template-columns: 1fr;
  }
}

/* Gallery */
.pv-main-image {
  aspect-ratio: 1 / 1;
  border-radius: 16px;
  background: #f5f3ef;
  border: 1px solid rgba(46, 42, 36, 0.08);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pv-main-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.pv-no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #9e9e9e;
  font-size: 13px;
}
.pv-thumbs {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
}
.pv-thumb {
  flex: 0 0 56px;
  height: 56px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid transparent;
  background: #f5f3ef;
  padding: 0;
  cursor: pointer;
  transition: border-color 0.15s;
}
.pv-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.pv-thumb--active {
  border-color: var(--v-primary-base);
}

/* Facts */
.pv-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pv-price {
  margin-top: 14px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.pv-price__value {
  font-size: 28px;
  font-weight: 700;
  color: #2e2a24;
  letter-spacing: -0.5px;
}
.pv-price__note {
  font-size: 13px;
  color: #8a8478;
}
.pv-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 16px;
}
.pv-stat {
  background: #faf8f5;
  border: 1px solid rgba(46, 42, 36, 0.07);
  border-radius: 12px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
}
.pv-stat__label {
  font-size: 12px;
  color: #8a8478;
}
.pv-stat__value {
  font-size: 20px;
  font-weight: 700;
  color: #2e2a24;
  font-variant-numeric: tabular-nums;
}
.pv-details {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
}
.pv-details dt {
  font-size: 12px;
  color: #8a8478;
}
.pv-details dd {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 500;
  color: #2e2a24;
}

/* Sections */
.pv-section {
  margin-top: 24px;
}
.pv-section__title {
  font-size: 14px;
  font-weight: 700;
  color: #2e2a24;
  margin-bottom: 10px;
  display: flex;
  align-items: baseline;
  gap: 10px;
}
.pv-section__hint {
  font-size: 12px;
  font-weight: 400;
  color: #8a8478;
}
.pv-text {
  font-size: 14px;
  line-height: 1.6;
  color: #4a453d;
  white-space: pre-line;
  margin-bottom: 8px;
}

/* Variants table */
.pv-table-wrap {
  border: 1px solid rgba(46, 42, 36, 0.08);
  border-radius: 14px;
  overflow-x: auto;
}
.pv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.pv-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #8a8478;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: #faf8f5;
  padding: 10px 14px;
}
.pv-table td {
  padding: 11px 14px;
  border-top: 1px solid rgba(46, 42, 36, 0.06);
  color: #2e2a24;
  white-space: nowrap;
}
.pv-table th.text-right,
.pv-table td.text-right {
  text-align: right;
}
.pv-table tbody tr {
  cursor: pointer;
  transition: background 0.12s;
}
.pv-table tbody tr:hover {
  background: #faf8f5;
}
.pv-row--active,
.pv-row--active:hover {
  background: rgba(202, 181, 150, 0.18) !important;
}
.pv-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-right: 8px;
  vertical-align: -2px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.pv-strike {
  color: #a39d92;
  text-decoration: line-through;
  margin-right: 6px;
  font-size: 13px;
}
.pv-stock {
  display: inline-block;
  min-width: 40px;
  text-align: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.pv-stock--ok {
  background: rgba(76, 154, 106, 0.12);
  color: #3b7d55;
}
.pv-stock--low {
  background: rgba(224, 164, 88, 0.16);
  color: #a86b1f;
}
.pv-stock--out {
  background: rgba(216, 86, 74, 0.12);
  color: #c0453a;
}
.pv-code {
  font-size: 12px;
  background: #f5f3ef;
  color: #5c564c;
  padding: 2px 6px;
  border-radius: 6px;
}

.pv-actions {
  display: flex;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid rgba(46, 42, 36, 0.08);
}
</style>
